import { responseService } from '@/services/checklists/response.service';
import { InspectionRequest } from '@/services/types/inspection.types';

/**
 * Busca as respostas do checklist da inspeção e mapeia para o formato
 * `questionarioData` esperado por `transformLaudoWizardToLaudoData`.
 *
 * Usado pela geração on-the-fly do PDF (preview do banco/prestadora,
 * visualização pós-criação) — replica o que o TransmissaoStep faz no fluxo
 * normal de transmissão.
 */
export async function fetchChecklistQuestionarioData(
  inspection: InspectionRequest,
): Promise<any[]> {
  const firstItem = inspection.items?.[0];
  const inspectionItemId = inspection.id;
  const checklistId = firstItem?.checklists?.[0]?.checklistId;

  if (!inspectionItemId || !checklistId) {
    return [];
  }

  let checklistResponses: any[] = [];

  try {
    const responses = await responseService.getResponsesByInspectionId(checklistId, inspectionItemId);
    const rawResponses = Array.isArray(responses) ? responses : [];

    // A API pode retornar ChecklistResponse[] com `answers[]` (precisa flatten)
    // ou já no formato processado (com `.question` e `.answer`).
    if (rawResponses.length > 0 && rawResponses[0].answers) {
      for (const resp of rawResponses) {
        if (resp.answers && Array.isArray(resp.answers)) {
          for (const ans of resp.answers) {
            checklistResponses.push({
              question: {
                id: ans.questionId,
                title: ans.questionId,
                answerType: ans.questionAnswerType || 'ShortAnswer',
                showInSimplifiedReport: true,
              },
              answer: ans,
            });
          }
        }
      }
    } else {
      checklistResponses = rawResponses;
    }
  } catch (err) {
    console.warn('[fetchChecklistQuestionarioData] Falha ao buscar respostas:', err);
    return [];
  }

  return checklistResponses.map((response: any) => {
    const answerType = response.question?.answerType || 'Text';
    const answer = response.answer;
    const showInReport = response.question?.showInSimplifiedReport ?? true;

    const mappedItem: any = {
      id: response.question?.id || '',
      pergunta: response.question?.title || '',
      answerType,
      isNotApplicable: false,
      showInSimplifiedReport: showInReport,
      sectionName: response.question?.sectionName || response.sectionName || undefined,
      sectionOrder: response.question?.sectionOrder ?? response.sectionOrder ?? undefined,
    };

    switch (answerType) {
      case 'ShortAnswer':
      case 'LongAnswer':
        mappedItem.resposta = answer?.textValue || '';
        break;
      case 'YesNo':
      case 'RadioButton':
        mappedItem.resposta = answer?.selectedOption || '';
        break;
      case 'Checkbox':
        mappedItem.respostas = answer?.selectedOptions || [];
        break;
      case 'Date':
        mappedItem.data = answer?.dateValue || null;
        break;
      case 'NumericInteger':
      case 'NumericDecimal':
      case 'Currency':
        mappedItem.valor = answer?.numericValue ?? null;
        break;
      case 'Photo':
        mappedItem.photos = answer?.photos || [];
        break;
      case 'File':
        mappedItem.files = answer?.files || [];
        break;
      case 'Table':
        mappedItem.tableData = answer?.tableData || [];
        break;
      default:
        mappedItem.resposta = String(answer?.textValue || '');
    }

    if (answer?.notes) {
      mappedItem.notes = answer.notes;
    }

    return mappedItem;
  });
}
